import { cadenceSlots, nextSlotGroups, fmtShort } from '../lib/cadence.js';

// Session 15: the cadence type choice and the monthly fields, shared by
// Add new client and Settings → Cadence. Validated again by the RPCs.
// Session 15b: posts on each date, exact date vs "first <weekday> on or
// after", an optional kick-off batch, and a preview grouped by date.

export const MONTHLY_DEFAULTS = { month_days: [1, 15], publish_weekday: 'wed', posts_per_date: 1 };

export function CadenceTypeChoice({ value, onChange }) {
  return (
    <div className="field">
      <span className="field-label">How often</span>
      <label className="check"><input type="radio" name="cadence-type" checked={value !== 'monthly'} onChange={() => onChange('weekly')} /> <span>Every week</span></label>
      <label className="check"><input type="radio" name="cadence-type" checked={value === 'monthly'} onChange={() => onChange('monthly')} /> <span>On set dates each month</span></label>
    </div>
  );
}

// The minimum days to the first post / kick-off: 7 for agency_only, else 14.
// Session 55 Part B: fast start (a client onboarded by hand) also allows 7 days.
export const minLeadDays = (mode, fastStart = false) => (mode === 'agency_only' || fastStart === true ? 7 : 14);

// monthDays: number[]; weekday: 'mon'..'sun' or '' (exact date);
// postsPerDate: 1..4; kickoff: ISO date or ''; kickoffMin: ISO date;
// previewFrom: ISO date
export function MonthlyFields({ monthDays, weekday, postsPerDate = 1, kickoff = '', onDays, onWeekday, onPostsPerDate,
                                onKickoff, kickoffMin, weekdays, previewFrom, lastWeekday }) {
  const picked = new Set(monthDays);
  const toggle = (n) => {
    if (picked.has(n)) onDays(monthDays.filter((x) => x !== n));
    else if (monthDays.length < 4) onDays([...monthDays, n].sort((a, b) => a - b));
  };
  const exact = !weekday;
  const cad = { type: 'monthly', month_days: monthDays, publish_weekday: weekday, posts_per_date: postsPerDate,
                kickoff_date: kickoff || null };
  const preview = monthDays.length ? nextSlotGroups(cad, previewFrom, 6) : [];
  const wdLabel = weekdays.find(([k]) => k === weekday)?.[1] || weekday;
  const daysText = monthDays.join(' and ');
  return (
    <>
      <div className="field">
        <span className="field-label">Days of the month</span>
        <div className="day-chips" role="group" aria-label="Days of the month">
          {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
            <button key={n} type="button" className="btn day-chip" aria-pressed={picked.has(n)}
                    disabled={!picked.has(n) && monthDays.length >= 4} onClick={() => toggle(n)}>{n}</button>
          ))}
        </div>
        <span className={`field-hint ${monthDays.length ? '' : 'err'}`}>
          {!monthDays.length ? 'Pick 1 to 4 days.'
            : exact ? `Posts go live on day ${daysText} of each month, weekends included. Up to 4 days.`
              : `Each post goes live on the first ${wdLabel} on or after day ${daysText}. Up to 4 days.`}
        </span>
      </div>
      <div className="inline-fields">
        <div className="field">
          <span className="field-label">Publish on</span>
          <label className="check"><input type="radio" name="monthly-weekday" checked={exact} onChange={() => onWeekday('')} /> <span>The exact date</span></label>
          <label className="check">
            <input type="radio" name="monthly-weekday" checked={!exact} onChange={() => onWeekday(lastWeekday || MONTHLY_DEFAULTS.publish_weekday)} />
            <span>The first</span>
            <select value={weekday || lastWeekday || MONTHLY_DEFAULTS.publish_weekday} disabled={exact}
                    onChange={(e) => onWeekday(e.target.value)} aria-label="Weekday">
              {weekdays.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <span>on or after</span>
          </label>
        </div>
        <div className="field">
          <span className="field-label">Posts on each date</span>
          <select value={postsPerDate} onChange={(e) => onPostsPerDate(Number(e.target.value))}>
            {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
      <div className="field">
        <label className="check">
          <input type="checkbox" checked={!!kickoff} onChange={(e) => onKickoff(e.target.checked ? kickoffMin : '')} />
          <span>Start with a kick-off batch on</span>
          <input type="date" min={kickoffMin} value={kickoff} disabled={!kickoff} onChange={(e) => onKickoff(e.target.value)}
                 aria-label="Kick-off date" />
        </label>
        <span className="field-hint">
          A one-time batch of {postsPerDate} post{postsPerDate === 1 ? '' : 's'} before the regular dates. It can be any day, at least {
          kickoffMin ? fmtShort(kickoffMin) : 'the minimum'} or later.
          {kickoff && kickoff < kickoffMin && <span className="err"> Pick {fmtShort(kickoffMin)} or later.</span>}
        </span>
      </div>
      {preview.length > 0 && (
        <div className="banner banner-info">
          Next publish dates: {preview.map((g) => `${fmtShort(g.date)} · ${g.count} post${g.count === 1 ? '' : 's'}`).join(', ')}…
        </div>
      )}
    </>
  );
}

// Valid monthly first post dates (Add new client picker): every slot date,
// kick-off included, distinct.
export function monthlyFirstDates(cad, fromIso, toIsoStr) {
  return [...new Set(cadenceSlots({ type: 'monthly', ...cad }, fromIso, toIsoStr).map(([d]) => d))];
}
